use '@use-gpu/wgsl/use/types'::{ Light, SurfaceFragment };

@optional @link fn sampleShadow(uv: vec2<f32>, index: u32, level: f32) -> f32 { return 1.0; }

@export fn applySpotShadow(
  light: Light,
  surface: SurfaceFragment,
) -> f32 {
  let index = u32(light.shadowMap);

  let toLight = light.position.xyz - surface.position.xyz;
  let lightRadius = length(toLight);

  let surfacePos = surface.position.xyz + surface.normal.xyz * light.shadowBias.z * lightRadius;
  let pos = light.into * vec4<f32>(surfacePos, 1.0);

  let n = dot(surface.normal.xyz, light.normal.xyz);
  let slope = sqrt(1 - n * n) / abs(n);

  let z = abs(pos.z);
  let refDepth = dot(vec2<f32>(1.0, 1.0/z), light.shadowDepth);
  let depth = refDepth * (1.0 + (light.shadowBias.x + slope * light.shadowBias.y) * z);
  if (depth < 0.0) { return 1.0; }

  let blur = light.shadowBlur;
  let size = (light.shadowUV.zw - light.shadowUV.xy) * SHADOW_PAGE;

  let res = f32(blur) / (2.0 * size);
  let uvm = clamp(pos.xy / abs(pos.z) * light.opts.z * .5 + .5, vec2<f32>(res), vec2<f32>(1.0 - res));
  let uv = mix(light.shadowUV.xy, light.shadowUV.zw, uvm);

  var s = 0.0;
  if (blur >= 4) {
    for (var y = -1.5; y <= 1.5; y += 1.0) {
      for (var x = -1.5; x <= 1.5; x += 1.0) {
        s += sampleShadow(uv + vec2<f32>(x, y) / SHADOW_PAGE, index, depth);
      }
    }
    s /= 16.0;
  }
  else if (blur == 3) {
    for (var y = -1.0; y <= 1.0; y += 1.0) {
      for (var x = -1.0; x <= 1.0; x += 1.0) {
        s += sampleShadow(uv + vec2<f32>(x, y) / SHADOW_PAGE, index, depth);
      }
    }
    s /= 9.0;
  }
  else if (blur == 2) {
    for (var y = -0.5; y <= 0.5; y += 1.0) {
      for (var x = -0.5; x <= 0.5; x += 1.0) {
        s += sampleShadow(uv + vec2<f32>(x, y) / SHADOW_PAGE, index, depth);
      }
    }
    s /= 4.0;
  }
  else {
    s += sampleShadow(uv, index, depth);
  }

  return s;
};
